<template>
  <pt-sheet
    :title="
      selectedVendor ? `Subscriptions for ${selectedVendor.name}` : 'Loading...'
    "
  >
    <v-data-table
      :headers="headers"
      :items="subscriptions"
      :loading="subscriptionsLoading"
      hide-default-footer
      disable-sort
      :options="tableOptions"
      show-expand
    >
      <template v-slot:item.subscriptionType="{ item }">
        {{ item.subscriptionType }}
      </template>
      <template v-slot:item.startDate="{ item }">
        {{ item.startDate | formatDate("L") }}
      </template>
      <template v-slot:item.renewalDate="{ item }">
        {{ item.renewalDate | formatDate("L") }}
      </template>
      <template v-slot:item.autoRenew="{ item }">
        {{ item.autoRenew ? "Yes" : "No" }}
      </template>
      <template v-slot:item.amount="{ item }">
        {{ item.amount | formatPrice }}
      </template>
      <template v-slot:item.metadata="{ item }">
        <div v-for="entry in item.metadata" :key="entry.key">
          {{ entry.key }}: {{ entry.value }}
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn small @click="qaActions(item)">
          QA Actions
        </v-btn>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td colspan="9" class="pl-12">
          <v-data-table
            class="mt-4 mb-8 ml-12"
            :headers="bpHeaders"
            :items="item.billingPeriods"
            :loading="subscriptionsLoading"
            hide-default-footer
            disable-sort
            :options="tableOptions"
            dense
          >
            <template v-slot:item.paymentStatus="{ item }">
              {{ item.paymentStatus ? item.paymentStatus : "No Payment" }}
            </template>
            <template v-slot:item.startDate="{ item }">
              {{ item.startDate | formatDate("L") }}
            </template>
            <template v-slot:item.endDate="{ item }">
              {{ item.endDate | formatDate("L") }}
            </template>
            <template v-slot:item.renewalDate="{ item }">
              {{ item.renewalDate | formatDate("L") }}
            </template>
            <template v-slot:item.fee="{ item }">
              {{ item.fee | formatPrice }}
            </template>
            <template v-slot:item.metadata="{ item }">
              <div v-for="entry in item.metadata" :key="entry.key">
                {{ entry.key }}: {{ entry.value }}
              </div>
            </template>
          </v-data-table>
        </td>
      </template>
    </v-data-table>

    <v-dialog
      v-if="qaActionsDialog"
      v-model="qaActionsDialog"
      persistent
      max-width="800px"
    >
      <v-card>
        <v-card-title>
          <span class="headline"
            >QA Actions for Subscription ID
            {{ this.editedSubscription.id }}</span
          >
        </v-card-title>

        <v-card-text>
          <v-card elevation="1" color="colorNeutralsNavy50">
            <v-card-title
              ><span class="title">Expire Subscription</span></v-card-title
            >
            <v-card-text>
              <v-alert type="info">
                Expiring the subscription will change the payment method to be
                invalid and force the subscription through the dunning cycle.
                The specific order of events is as follows:
                <ul>
                  <li>
                    Append "BROKEN" to the end of the payment method so it
                    becomes invalid.
                  </li>
                  <li>
                    If there is an active billing period, set its end date to
                    the current date.
                  </li>
                  <li>
                    Create a new billing period and attempt a payment, which
                    should fail (except when the subscription price is $0 -
                    since there is no failure, expiring a $0 price subscription
                    will not work!).
                  </li>
                  <li>
                    Move the subscription start date, all the billing period
                    start and end dates, and all transaction dates 2 days in the
                    past.
                  </li>
                  <li>Attempt another payment.</li>
                  <li>Move the same dates another 1 days in the past.</li>
                  <li>
                    Make another run of the retry-states, which should trigger a
                    follow-up email (but shouldn't attempt a payment).
                  </li>
                  <li>Move the same dates to be another 7 days in the past.</li>
                  <li>Make a final payment attempt.</li>
                </ul>
              </v-alert>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="primary"
                @click="expireSubscription(editedSubscription.id)"
                :loading="expiringSubscription"
                >Expire Subscription
              </v-btn>
            </v-card-actions>
          </v-card>
          <v-card elevation="1" color="colorNeutralsNavy50" class="mt-4">
            <v-card-title
              ><span class="title">Change Renewal Date</span></v-card-title
            >
            <v-card-text>
              <v-alert type="info">
                Changing the renewal date will set the end date of the most
                recent billing period.
              </v-alert>
              <v-text-field v-model="renewalDate" label="Renewal Date" />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="primary"
                @click="changeRenewalDate()"
                :loading="changingRenewalDate"
                >Change Renewal Date
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn @click="qaActionsDialog = false"
            >Close QA Actions Dialog</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </pt-sheet>
</template>

<script>
import { get, post } from "@/services/request";
import {
  qaExpireSubscriptionAPI,
  qaSubscriptionRenewalDateAPI,
  vpSubscriptionsAPI,
  currentVendorAPI
} from "@/services/api";
import hasCurrentUser from "@/mixins/has-current-user";
import hasNotifications from "@/mixins/has-notifications";
import PtSheet from "@/components/PtSheet.vue";
import { formatDate, formatPrice } from "@/services/format";

const headers = [
  { text: "Subscription ID", value: "id" },
  { text: "Subscriber ID", value: "subscriberId" },
  { text: "Type", value: "subscriptionType" },
  { text: "Start Date", value: "startDate" },
  { text: "Renewal Date", value: "renewalDate" },
  { text: "Amount", value: "amount" },
  { text: "Auto Renew", value: "autoRenew" },
  { text: "Metadata", value: "metadata" },
  { value: "actions" }
];

const bpHeaders = [
  { text: "Billing Period ID", value: "id" },
  { text: "Payment Status", value: "paymentStatus" },
  { text: "Payment Type", value: "paymentType" },
  { text: "Start Date", value: "startDate" },
  { text: "End Date", value: "endDate" },
  { text: "Refund Date", value: "refundDate" },
  { text: "Transaction Amount", value: "fee" },
  { text: "Metadata", value: "metadata" }
];

const tableOptions = {
  itemsPerPage: -1
};

export default {
  name: "QASubscriptions",
  components: { PtSheet },
  mixins: [hasCurrentUser, hasNotifications],
  data: () => ({
    tableOptions,
    headers,
    bpHeaders,
    selectedVendor: null,
    subscriptionsLoading: false,
    subscriptions: [],
    editedSubscription: null,
    qaActionsDialog: false,
    renewalDate: null,
    expiringSubscription: false,
    changingRenewalDate: false
  }),
  created() {
    this.loadSubscriptions();
    this.getCurrentVendor();
  },
  filters: {
    formatDate,
    formatPrice
  },
  methods: {
    loadSubscriptions() {
      this.subscriptionsLoading = true;
      get(vpSubscriptionsAPI)
        .then(response => {
          if (response && response.data) {
            this.subscriptions = response.data || [];
          }
          this.subscriptionsLoading = false;
        })
        .catch(() => {
          this.showMessage({
            text: "Unable to load subscriptions",
            mode: "error"
          });
          return Promise.reject();
        });
    },
    getCurrentVendor() {
      get(currentVendorAPI)
        .then(response => {
          if (response && response.data) {
            this.selectedVendor = response.data;
          }
        })
        .catch(() => {
          this.showMessage({
            text: "Unable to load current vendor",
            mode: "error"
          });
          return Promise.reject();
        });
    },
    qaActions(subscription) {
      this.editedSubscription = subscription;
      this.qaActionsDialog = true;
      this.renewalDate = new Date(subscription.renewalDate).toLocaleString();
    },
    expireSubscription(subscriptionId) {
      let request = {
        subscriptionId: subscriptionId
      };
      this.expiringSubscription = true;
      post(qaExpireSubscriptionAPI, request)
        .then(
          () => {
            let successMessage = `Subscription ID ${subscriptionId} is now expired`;
            console.log(successMessage);
            this.showMessage(successMessage);
            this.loadSubscriptions();
          },
          err => {
            console.error(err);
            this.showMessage({
              mode: "error",
              text: "Failed to expire subscription"
            });
          }
        )
        .finally(() => {
          this.expiringSubscription = false;
          this.qaActionsDialog = false;
        });
    },
    changeRenewalDate() {
      let newRenewalDate = new Date(this.renewalDate).getTime();
      this.changingRenewalDate = true;
      post(qaSubscriptionRenewalDateAPI.getUrl(this.editedSubscription.id), {
        renewalDate: newRenewalDate
      })
        .then(response => {
          if (response) {
            this.loadSubscriptions();
          }
        })
        .catch(() => {
          this.showMessage({
            text: "Unable to change renewal date",
            mode: "error"
          });
        })
        .finally(() => {
          this.changingRenewalDate = false;
          this.qaActionsDialog = false;
        });
    }
  }
};
</script>
<style scoped lang="scss">
.v-application .v-alert {
  color: white;
  font-size: 16px;
  font-weight: 300;

  &.theme--dark {
    ::v-deep a {
      color: white;
    }
  }
}
.v-alert__icon.v-icon {
  align-self: unset !important;
}
</style>
